import { createStore } from 'zustand/vanilla'
import { readJson, writeJson } from '../services/storage'

// ─────────────────────────────────────────────────────────────────────────────
// Map layer visibility.
//
// Distinct from useTransitStore.transitFilter, which selects WHICH VEHICLES exist. This
// selects WHAT IS DRAWN about them, and the two compose: a layer shows only what the filter
// admits, so bus stops appear when `busStops` is on AND the filter isn't subway-only.
//
// Base layers are the map as it has always looked, on by default (`buildings` excepted —
// see its note below, it is an opt-in view rather than the map's own). Analysis layers
// repaint vehicles with derived intelligence and are off by default, so first launch stays
// "where is my train" and the telemetry is opt-in.
//
// Persisted to localStorage as settings.ts does; see the note there.
// ─────────────────────────────────────────────────────────────────────────────

const LAYERS_KEY = 'transit_layers'

export interface LayerState {
  // Base
  routeLines: boolean
  stations: boolean
  busStops: boolean
  /**
   * Extrude the basemap's building footprints.
   *
   * Off by default, unlike the other base layers: it is the one that costs fill rate on a
   * phone, and it can hide a vehicle behind a block. On, it gives a street-level ride the
   * context a bus route needs — which side of the avenue, which corner — that a flat map
   * cannot show.
   */
  buildings: boolean
  // Analysis
  /** Recolor vehicles by schedule deviation instead of route color. */
  delays: boolean
  /** Show the upstream-feed freshness readout over the map. */
  feedHealth: boolean
}

const DEFAULTS: LayerState = {
  routeLines: true,
  stations: true,
  busStops: true,
  buildings: false,
  delays: false,
  feedHealth: false,
}

export type LayerKey = keyof LayerState

function loadLayers(): LayerState {
  return readJson(LAYERS_KEY, { ...DEFAULTS }, decodeLayers)
}

function decodeLayers(value: unknown): LayerState | undefined {
  if (typeof value !== 'object' || value === null) return undefined
  const obj = value as Record<string, unknown>
  const out = { ...DEFAULTS }
  for (const key of Object.keys(DEFAULTS) as LayerKey[]) {
    if (typeof obj[key] === 'boolean') out[key] = obj[key] as boolean
  }
  return out
}

function persistLayers(s: LayerState): void {
  writeJson(LAYERS_KEY, s)
}

interface LayersStore extends LayerState {
  toggleLayer: (key: LayerKey) => void
  setLayer: (key: LayerKey, value: boolean) => void
  resetLayers: () => void
}

// `delays` is the only layer that repaints vehicle bodies. A second one would have to be
// mutually exclusive with it: a body carries exactly one color, so two active tint layers
// leave the legend unable to say what it means.
export const useLayersStore = createStore<LayersStore>((set) => ({
  ...loadLayers(),

  setLayer: (key, value) => set({ [key]: value } as Partial<LayerState>),
  toggleLayer: (key) => set((s) => ({ [key]: !s[key] } as Partial<LayerState>)),
  resetLayers: () => set({ ...DEFAULTS }),
}))

useLayersStore.subscribe((state, prev) => {
  const changed = (Object.keys(DEFAULTS) as LayerKey[]).some((k) => state[k] !== prev[k])
  if (!changed) return
  const toPersist = {} as LayerState
  for (const k of Object.keys(DEFAULTS) as LayerKey[]) toPersist[k] = state[k]
  persistLayers(toPersist)
})
